import { useEffect } from 'react';
import { Route, Routes as RoutesWrapper, useLocation } from 'react-router-dom';

// Custom
import { PrivateRoute, PublicRoute } from '@/main/routes';
import {
  RoutesConstants,
  getPublicRoutes
} from '@/main/routes/routesConstants';
import {
  PREVIOUS_ROUTE,
  isUserAuthenticated,
  saveStringStorage
} from '@/main/services/storage';

// Import Public Routes
import ConfirmPasswordScreen from '@/main/screens/account/confirmPassword/confirmPasswordScreen';
import LoginScreen from '@/main/screens/login/loginScreen';

// Import Private Routes
// Default
import NoAccessPermissionScreen from '@/main/screens/noAccessPermission/noAccessPermissionScreen';

import HomepageRoute from '@/main/routes/homepageRoute';
import SearchClaimScreen from '@/main/screens/claim/searchClaim/searchClaimScreen';
import HomeScreen from '@/main/screens/home/homeScreen';
import IntegrationQueueScreen from '@/main/screens/integration/integrationQueue/integrationQueueScreen';
import IntegrationRegisterScreen from '@/main/screens/integration/integrationRegisterScreen/integrationRegisterScreen';
import IntegrationsLogScreen from '@/main/screens/integration/integrationsLog/integrationsLogScreen';
import MappingCodesScreen from '@/main/screens/integration/mappingCodes/mappingCodesScreen';
import SearchIntegrationScreen from '@/main/screens/integration/searchIntegration/searchIntegrationScreen';
import ImportInventoryScreen from '@/main/screens/inventory/importInventory/importInventoryScreen';
import InventoryRegisterScreen from '@/main/screens/inventory/inventoryRegisterScreen/inventoryRegisterScreen';
import CreateorderScreen from '@/main/screens/order/createorder/createorderScreen';
import MappingPartsScreen from '@/main/screens/order/mappingParts/mappingPartsScreen';
import PreQuote from '@/main/screens/order/preQuote/preQuote';
import OrderScreen from '@/main/screens/order/viewOrder/orderScreen';
import EvaluationSupplierScreen from '@/main/screens/public/evaluation/evaluationSupplierScreen';
import HomepageAboutScreen from '@/main/screens/public/homepage/about/homepageAbout';
import HomepageBodyshopScreen from '@/main/screens/public/homepage/bodyshop/homepageBodyshop';
import HomepageCookies from '@/main/screens/public/homepage/cookies/homepageCookies';
import HomePageDemoScreen from '@/main/screens/public/homepage/demo/homepageDemo';
import HomepageScreen from '@/main/screens/public/homepage/homepage/homepage';
import HomePageInsurerScreen from '@/main/screens/public/homepage/insurer/homepageInsurer';
import HomepagePrivacyPolicy from '@/main/screens/public/homepage/privacyPolicy/homepagePrivacyPolicy';
import HomepageSupplierScreen from '@/main/screens/public/homepage/supplier/homepageSupplier';
import RegisterPublicBodyshopScreen from '@/main/screens/public/register/registerPublicBodyshopScreen/registerPublicBodyshopScreen';
import RegisterPublicSupplierScreen from '@/main/screens/public/register/registerPublicSupplierScreen/registerPublicSupplierScreen';
import CartSearchPendingToAnswer from '@/main/screens/quotation/pendingToAnswer/searchPendingToAnswer';
import BodyshopRegisterScreen from '@/main/screens/registration/bodyshop/bodyshopRegisterScreen/bodyshopRegisterScreen';
import BodyshopScreen from '@/main/screens/registration/bodyshop/bodyshopScreen';
import HolidaysScreen from '@/main/screens/registration/holidays/holidaysScreen';
import InsurerRegisterScreen from '@/main/screens/registration/insurer/insurerRegisterScreen/insurerRegisterScreen';
import InsurerScreen from '@/main/screens/registration/insurer/insurerScreen';
import PackWorkflowScreen from '@/main/screens/registration/packWorkflow/packWorkflowScreen';
import PartsCatalogScreen from '@/main/screens/registration/partsCatalog/partsCatalogScreen';
import SupplierRegisterScreen from '@/main/screens/registration/supplier/supplierRegisterScreen/supplierRegisterScreen';
import SupplierScreen from '@/main/screens/registration/supplier/supplierScreen';
import UserRegistrationScreen from '@/main/screens/registration/user/userRegistration/userRegistrationScreen';
import UserScreen from '@/main/screens/registration/user/userScreen';
import UsergroupRegisterScreen from '@/main/screens/registration/usergroup/usergroupRegister/usergroupRegisterScreen';
import UsergroupScreen from '@/main/screens/registration/usergroup/usergroupScreen';
import SupervisorScreen from '@/main/screens/registration/supervisor/supervisorScreen';
import BillingOrderScreen from '@/main/screens/reports/billingOrder/billingOrderScreen';
import OrderReportScreen from '@/main/screens/reports/orderReport/orderReportScreen';
import OrderReportScheduleScreen from '@/main/screens/reports/orderReportSchedule/orderReportSchedule';
import RegisterDynamicReport from '@/main/screens/reports/dynamicReport/register/registerDynamicReport';
import RequoteTraceabilityScreen from '@/main/screens/reports/requoteTraceability/requoteTraceabilityScreen';
import ShippingCostScreen from '@/main/screens/reports/shippingCost/shippingCostScreen';
import SupplierPerformanceScreen from '@/main/screens/reports/supplierPerformance/supplierPerformanceScreen';
import ManagePackagesScreen from '../../screens/order/managePackages/managePackagesScreen';
import DynamicReportScreen from '@/main/screens/reports/dynamicReport/dynamicReport';
import ReportTemplateScreen from '@/main/screens/registration/reportTemplate/searchReportTemplate/reportTemplateScreen';
import RegisterReportTemplateScreen from '@/main/screens/registration/reportTemplate/registerReportTemplate/registerReportTemplateScreen';

import SearchValuatorScreen from '@/main/screens/registration/valuator/searchValuatorScreen';
import SupervisorRegisterScreen from '@/main/screens/registration/supervisor/supervisorRegisterScreen/supervisorRegisterScreen';
import ValuatorRegisterScreen from '@/main/screens/registration/valuator/valuatorRegisterScreen/valuatorRegisterScreen';
import EmailTemplatesScreen from '@/main/screens/registration/emailTemplates/searchEmailTemplates/searchEmailTemplatesScreen';
import RegisterEmailTemplateScreen from '@/main/screens/registration/emailTemplates/registerEmailTemplate/registerEmailTemplateScreen';
import { AnnouncementRegisterScreen } from '@/main/screens/registration/announcement/announcementRegisterScreen/announcementRegisterScreen';
import { AnnouncementScreen } from '@/main/screens/registration/announcement/announcementScreen';
import TireBrandScreen from '@/main/screens/registration/tireBrand/tireBrandScreen';
import CreateAnswerScreenWrapper from '@/main/screens/quotation/createAnswer/createAnswerScreenWrapper';
import RegionScreen from '@/main/screens/registration/region/regionScreen';
import TermsOfUse from '@/main/screens/registration/termsOfUse/termsOfUse';
import CancelledPartsScreen from '@/main/screens/order/cancelledParts/cancelledPartsScreen';
import ReassignScreen from '@/main/screens/order/reassignOrder/reassignScreen';
import BodyshopRateManagementScreen from '@/main/screens/registration/bodyshopRatesManagement/bodyshopRateManagementScreen';
import SystemGuideSearchScreen from '@/main/screens/registration/systemGuide/systemGuideSearchScreen';
import SearchApprovalMatrix from '@/main/screens/registration/approvalMatrix/searchApprovalMatrix';
import RegisterApprovalMatrix from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/registerApprovalMatrix';

function Routes() {
  const location = useLocation();
  const isLogged = isUserAuthenticated();

  useEffect(() => {
    const urlSystem = `${location.pathname}${location.search}`;
    if (!isUserAuthenticated() && !getPublicRoutes().includes(urlSystem)) {
      saveStringStorage(
        PREVIOUS_ROUTE,
        `${window.location.origin}/#${urlSystem}`
      );
    }
  }, [location.pathname]);

  return (
    <RoutesWrapper>
      {/* #region HomePage Screens */}
      <Route element={<HomepageRoute />}>
        {!isLogged && (
          <Route path={RoutesConstants.home} element={<HomepageScreen />} />
        )}
        <Route
          path={RoutesConstants.homepageAbout}
          element={<HomepageAboutScreen />}
        />
        <Route
          path={RoutesConstants.homepageBodyshop}
          element={<HomepageBodyshopScreen />}
        />
        <Route
          path={RoutesConstants.homepageDemo}
          element={<HomePageDemoScreen />}
        />
        <Route
          path={RoutesConstants.homepageInsurer}
          element={<HomePageInsurerScreen />}
        />
        <Route
          path={RoutesConstants.homepageSupplier}
          element={<HomepageSupplierScreen />}
        />
        <Route
          path={RoutesConstants.homepagePrivacyPolicy}
          element={<HomepagePrivacyPolicy />}
        />
        <Route
          path={RoutesConstants.homepageCookies}
          element={<HomepageCookies />}
        />
      </Route>
      {/* #endregion */}

      {/* Public Routes */}
      <Route element={<PublicRoute />}>
        <Route path={RoutesConstants.login} element={<LoginScreen />} />
        {[
          RoutesConstants.accountResetPassword,
          RoutesConstants.accountNewAccount
        ].map((route: string) => (
          <Route key={route} path={route} element={<ConfirmPasswordScreen />} />
        ))}
        <Route
          path={RoutesConstants.evaluationSupplierPublic}
          element={<EvaluationSupplierScreen />}
        />
        <Route
          path={RoutesConstants.supplierRegisterPublic}
          element={<RegisterPublicSupplierScreen />}
        />
        <Route
          path={RoutesConstants.bodyshopRegisterPublic}
          element={<RegisterPublicBodyshopScreen />}
        />
      </Route>

      {/* Private Routes */}
      <Route element={<PrivateRoute />}>
        <Route path={RoutesConstants.home} 
          element={<HomeScreen />}
        />

        {/* Module - Registration */}
        <Route
          path={RoutesConstants.registrationSearchBodyshop}
          element={<BodyshopScreen />}
        />
        <Route
          path={RoutesConstants.registrationRegisterBodyshop}
          element={<BodyshopRegisterScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchInsurer}
          element={<InsurerScreen />}
        />
        <Route
          path={RoutesConstants.registrationRegisterInsurer}
          element={<InsurerRegisterScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchSupplier}
          element={<SupplierScreen />}
        />
        <Route
          path={RoutesConstants.registrationRegisterSupplier}
          element={<SupplierRegisterScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchUser}
          element={<UserScreen />}
        />
        <Route
          path={RoutesConstants.registrationRegisterUser}
          element={<UserRegistrationScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchUsergroup}
          element={<UsergroupScreen />}
        />
        <Route
          path={RoutesConstants.registrationRegisterUsergroup}
          element={<UsergroupRegisterScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchSupervisor}
          element={<SupervisorScreen />}
        />

        <Route
          path={RoutesConstants.registrationPartsCatalog}
          element={<PartsCatalogScreen />}
        />

        <Route
          path={RoutesConstants.registrationPackWorkflow}
          element={<PackWorkflowScreen />}
        />

        <Route
          path={RoutesConstants.registrationHoliday}
          element={<HolidaysScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchReportTemplate}
          element={<ReportTemplateScreen />}
        />
        <Route
          path={RoutesConstants.registrationRegisterReportTemplate}
          element={<RegisterReportTemplateScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchValuator}
          element={<SearchValuatorScreen />}
        />

        <Route
          path={RoutesConstants.registrationRegisterValuator}
          element={<ValuatorRegisterScreen />}
        />

        <Route
          path={RoutesConstants.registrationRegisterSupervisor}
          element={<SupervisorRegisterScreen />}
        />
        <Route
          path={RoutesConstants.registrationEmailTemplates}
          element={<EmailTemplatesScreen />}
        />

        <Route
          path={RoutesConstants.registrationRegisterEmailTemplate}
          element={<RegisterEmailTemplateScreen />}
        />

        <Route
          path={RoutesConstants.registrationSearchAnnouncement}
          element={<AnnouncementScreen />}
        />

        <Route
          path={RoutesConstants.registrationRegisterAnnouncement}
          element={<AnnouncementRegisterScreen />}
        />

        <Route
          path={RoutesConstants.registrationTireBrand}
          element={<TireBrandScreen />}
        />

        <Route
          path={RoutesConstants.registrationRegion}
          element={<RegionScreen />}
        />

        <Route
          path={RoutesConstants.registrationTermsOfUse}
          element={<TermsOfUse />}
        />

        <Route
          path={RoutesConstants.registrationSearchBodyshopRates}
          element={<BodyshopRateManagementScreen />}
        />

        <Route 
          path={RoutesConstants.registrationSearchGuid}
          element={<SystemGuideSearchScreen />}
        />

        <Route 
          path={RoutesConstants.registrationSearchApprovalMatrix}
          element={<SearchApprovalMatrix />}
        />

        <Route 
          path={RoutesConstants.registrationRegisterApprovalMatrix}
          element={<RegisterApprovalMatrix />}
        />

        {/* Module - Order */}
        <Route
          path={RoutesConstants.orderCreateOrder}
          element={<CreateorderScreen />}
        />
        <Route
          path={RoutesConstants.orderSearchOrder}
          element={<OrderScreen />}
        />
        <Route path={RoutesConstants.orderPreQuote} element={<PreQuote />} />
        <Route
          path={RoutesConstants.orderReassignOrder}
          element={<ReassignScreen isBulk={false} />}
        />
        <Route
          path={RoutesConstants.orderReassignOrderBulk}
          element={<ReassignScreen isBulk={true} />}
        />
        <Route
          path={RoutesConstants.orderMappingParts}
          element={<MappingPartsScreen />}
        />
        <Route
          path={RoutesConstants.orderManagePackages}
          element={<ManagePackagesScreen />}
        />
        <Route
          path={RoutesConstants.reportOrderReportSchedule}
          element={<OrderReportScheduleScreen />}
        />
        <Route
          path={RoutesConstants.reportDynamicReportRegister}
          element={<RegisterDynamicReport />}
        />

        <Route
          path={RoutesConstants.partsCancelledBySupplier}
          element={<CancelledPartsScreen />}
        />

        {/* Module - Quotation */}
        <Route
          path={RoutesConstants.quotationCartPendingToAnswer}
          element={<CartSearchPendingToAnswer />}
        />
        <Route
          path={RoutesConstants.quotationCartCreateAnswer}
          element={<CreateAnswerScreenWrapper />}
        />

        {/* Module - Inventory */}
        <Route
          path={RoutesConstants.inventorySearchImport}
          element={<ImportInventoryScreen />}
        />

        <Route
          path={RoutesConstants.inventoryRegister}
          element={<InventoryRegisterScreen />}
        />

        {/* Module - Integration */}
        <Route
          path={RoutesConstants.integrationSearchIntegrationsLog}
          element={<IntegrationsLogScreen />}
        />
        <Route
          path={RoutesConstants.integrationSearchIntegration}
          element={<SearchIntegrationScreen />}
        />
        <Route
          path={RoutesConstants.integrationRegister}
          element={<IntegrationRegisterScreen />}
        />
        <Route
          path={RoutesConstants.integrationQueue}
          element={<IntegrationQueueScreen />}
        />
        <Route
          path={RoutesConstants.integrationMappingCodes}
          element={<MappingCodesScreen />}
        />

        {/* Module - Report */}
        <Route
          path={RoutesConstants.reportOrder}
          element={<OrderReportScreen />}
        />
        <Route
          path={RoutesConstants.reportBillingOrder}
          element={<BillingOrderScreen />}
        />
        <Route
          path={RoutesConstants.reportShippingCost}
          element={<ShippingCostScreen />}
        />
        <Route
          path={RoutesConstants.reportSupplierPerformance}
          element={<SupplierPerformanceScreen />}
        />
        <Route
          path={RoutesConstants.reportRequoteTraceability}
          element={<RequoteTraceabilityScreen />}
        />
        <Route
          path={RoutesConstants.dynamicReport}
          element={<DynamicReportScreen />}
        />

        {/* Module - Claim */}
        <Route
          path={RoutesConstants.claimSearchClaims}
          element={<SearchClaimScreen />}
        />

        {/* Default route */}
        <Route path="*" element={<NoAccessPermissionScreen />} />
      </Route>
    </RoutesWrapper>
  );
}

export { Routes };
